import { getAuth } from 'firebase/auth';
import type {
  TikTokAdsAdvertiser,
  TikTokAdsConnectApi,
  TikTokAdsConnectionStatus,
  TikTokAdsConnectionsResponse,
  TikTokAdsDeliveryPreviewResult,
  TikTokAdsDiscoveryResponse,
  TikTokAdsEntityPreviewResult,
  TikTokAdsRuntimeStatus,
  TikTokAdsSelectionResponse,
  TikTokAdsSyncResult,
} from '../types';

function getApiUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) return import.meta.env.VITE_BUILDER_API_URL;
  if (import.meta.env.VITE_AGENT_API_URL) return import.meta.env.VITE_AGENT_API_URL;

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8081';
  if (hostname.includes('devsegmentlyai')) return 'https://dev-api.segmently.ai';
  if (hostname.includes('stagesegmentlyai')) return 'https://stage-api.segmently.ai';
  return 'https://api.segmently.ai';
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null) as {
    error?: string | { message?: string };
    detail?: string;
    message?: string;
  } | null;
  if (typeof body?.error === 'string' && body.error.trim()) return body.error;
  if (typeof body?.error === 'object' && body.error?.message?.trim()) return body.error.message;
  if (body?.detail?.trim()) return body.detail;
  if (body?.message?.trim()) return body.message;
  return fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function connectionFromApi(value: unknown): TikTokAdsConnectionStatus {
  const raw = asRecord(value);
  const identity = asRecord(raw.connection);
  const rawState = typeof raw.state === 'string' ? raw.state : 'disconnected';
  const state = rawState === 'reauthorization_required' ? 'needs_reconnect' : rawState;
  return {
    connectionId: String(identity.connectionId ?? raw.connectionId ?? ''),
    state: state === 'connected' || state === 'needs_reconnect' ? state : 'disconnected',
    displayName: typeof raw.displayName === 'string' ? raw.displayName : undefined,
    apiEnvironment: raw.apiEnvironment === 'sandbox' ? 'sandbox' : 'production',
    credentialSource: raw.credentialSource === 'provider_sandbox'
      ? 'provider_sandbox'
      : 'oauth',
    grantedScopes: strings(raw.grantedPermissions ?? raw.grantedScopes),
    selectedAdvertiserIds: strings(raw.selectedAdvertiserIds ?? raw.selectedProviderAccountIds).sort(),
    sourceApiVersion: typeof raw.sourceApiVersion === 'string'
      ? raw.sourceApiVersion
      : 'tiktok-business-api/v1.3',
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date(0).toISOString(),
    lastSuccessfulPreviewAt: typeof raw.lastSuccessfulPreviewAt === 'string'
      ? raw.lastSuccessfulPreviewAt
      : undefined,
    lastSuccessfulManualSyncAt: typeof raw.lastSuccessfulManualSyncAt === 'string'
      ? raw.lastSuccessfulManualSyncAt
      : undefined,
  };
}

function runtimeFromApi(value: unknown): TikTokAdsRuntimeStatus {
  const raw = asRecord(value);
  return {
    connectorEnabled: raw.connectorEnabled === true,
    rollbackActive: raw.rollbackActive === true,
    manualSyncEnabled: raw.manualSyncEnabled === true,
    apiEnvironment: raw.apiEnvironment === 'sandbox' ? 'sandbox' : 'production',
    projectEligible: raw.projectEligible === true,
    credentialReady: raw.credentialReady === true,
    unavailableReason: typeof raw.unavailableReason === 'string' ? raw.unavailableReason : undefined,
  };
}

function advertiserFromApi(value: unknown, credentialSource: TikTokAdsConnectionStatus['credentialSource']): TikTokAdsAdvertiser {
  const raw = asRecord(value);
  const advertiser = asRecord(raw.advertiser);
  const account = asRecord(raw.account);
  const identity = asRecord(account.identity);
  const provenance = asRecord(account.accessProvenance);
  const accessContext = asRecord(provenance.accessContext);
  const advertiserId = String(advertiser.advertiserId ?? identity.providerAccountId ?? '');
  const accessKind = String(accessContext.kind ?? 'direct');
  return {
    advertiserId,
    displayName: typeof advertiser.displayName === 'string'
      ? advertiser.displayName
      : typeof account.displayName === 'string' ? account.displayName : undefined,
    currency: String(advertiser.currency ?? account.currency ?? ''),
    currencyExponent: typeof advertiser.currencyExponent === 'number'
      && Number.isInteger(advertiser.currencyExponent)
      ? advertiser.currencyExponent
      : 2,
    timeZone: String(advertiser.timeZone ?? account.timeZone ?? ''),
    status: advertiser.status === 'active' || advertiser.status === 'disabled'
      ? advertiser.status
      : 'unknown',
    selected: account.selected === true,
    accessPath: credentialSource === 'provider_sandbox'
      ? 'sandbox_token'
      : accessKind === 'business_center' ? 'business_center' : 'direct',
    businessCenterId: typeof accessContext.businessCenterId === 'string'
      ? accessContext.businessCenterId
      : undefined,
    observedAt: typeof provenance.observedAt === 'string' ? provenance.observedAt : undefined,
  };
}

function countsFromEntities(entities: Record<string, unknown>): TikTokAdsEntityPreviewResult['counts'] {
  const count = (key: string) => Array.isArray(entities[key]) ? entities[key].length : 0;
  return {
    campaigns: count('campaign'),
    adGroups: count('ad_group'),
    manualAds: count('ad'),
    smartPlusContainers: count('ad_container'),
    materials: count('creative_artifact'),
  };
}

export class TikTokAdsConnectService {
  constructor(private readonly apiUrl: string = getApiUrl()) {}

  private async authHeaders(): Promise<Record<string, string>> {
    const token = await getAuth().currentUser?.getIdToken();
    if (!token) throw new Error('User not authenticated');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }

  private connectionsPath(projectId: string): string {
    return `/api/projects/${encodeURIComponent(projectId)}/paid-ads/tiktok/connections`;
  }

  private connectionPath(projectId: string, connectionId: string): string {
    return `${this.connectionsPath(projectId)}/${encodeURIComponent(connectionId)}`;
  }

  private async request(path: string, init: RequestInit, fallback: string): Promise<unknown> {
    const response = await fetch(`${this.apiUrl}${path}`, {
      ...init,
      headers: {
        ...(await this.authHeaders()),
        ...(init.headers as Record<string, string> | undefined),
      },
    });
    if (!response.ok) throw new Error(await errorMessage(response, fallback));
    return response.status === 204 ? undefined : response.json();
  }

  async listConnections(projectId: string, signal?: AbortSignal): Promise<TikTokAdsConnectionsResponse> {
    const raw = asRecord(await this.request(
      this.connectionsPath(projectId),
      { method: 'GET', signal },
      'Failed to load TikTok Ads connections',
    ));
    return {
      connections: Array.isArray(raw.connections) ? raw.connections.map(connectionFromApi) : [],
      runtime: runtimeFromApi(raw.runtime),
    };
  }

  async connectSandbox(projectId: string, connectionId = 'sandbox'): Promise<void> {
    await this.request(
      `${this.connectionPath(projectId, connectionId)}/sandbox/connect`,
      { method: 'POST', body: '{}' },
      'Failed to connect the TikTok Ads sandbox',
    );
  }

  async getOAuthUrl(projectId: string, connectionId = 'default'): Promise<string> {
    const raw = asRecord(await this.request(
      `${this.connectionPath(projectId, connectionId)}/oauth/authorize`,
      { method: 'POST', body: '{}' },
      'Failed to start TikTok Ads authorization',
    ));
    if (typeof raw.authorizationUrl !== 'string') throw new Error('TikTok Ads authorization URL is missing');
    return raw.authorizationUrl;
  }

  async discoverAdvertisers(
    projectId: string,
    connection: TikTokAdsConnectionStatus,
    signal?: AbortSignal,
  ): Promise<TikTokAdsDiscoveryResponse> {
    const advertisers = new Map<string, TikTokAdsAdvertiser>();
    let checkpoint: Record<string, unknown> | undefined;
    for (let page = 0; page < 100; page += 1) {
      const raw = asRecord(await this.request(
        `${this.connectionPath(projectId, connection.connectionId)}/advertisers/discover`,
        {
          method: 'POST',
          body: JSON.stringify({ checkpoint, pageSize: 100 }),
          signal,
        },
        'Failed to discover TikTok Ads advertisers',
      ));
      const rows = Array.isArray(raw.advertisers) ? raw.advertisers : [];
      rows.forEach((row) => {
        const normalized = advertiserFromApi(row, connection.credentialSource);
        if (normalized.advertiserId) advertisers.set(normalized.advertiserId, normalized);
      });
      if (raw.complete === true) {
        return { advertisers: [...advertisers.values()], complete: true, warnings: strings(raw.warnings) };
      }
      checkpoint = asRecord(raw.checkpoint);
      if (Object.keys(checkpoint).length === 0) throw new Error('TikTok Ads advertiser pagination is incomplete');
    }
    throw new Error('TikTok Ads advertiser discovery exceeded its bounded page limit');
  }

  async selectAdvertisers(input: {
    projectId: string;
    connectionId: string;
    advertiserIds: string[];
  }): Promise<TikTokAdsSelectionResponse> {
    const raw = asRecord(await this.request(
      `${this.connectionPath(input.projectId, input.connectionId)}/advertisers/select`,
      { method: 'POST', body: JSON.stringify({ advertiserIds: input.advertiserIds }) },
      'Failed to save TikTok Ads advertiser selection',
    ));
    const record = connectionFromApi(raw);
    return { selectedAdvertiserIds: record.selectedAdvertiserIds };
  }

  async previewEntities(input: {
    projectId: string;
    connectionId: string;
    advertiserId: string;
    signal?: AbortSignal;
  }): Promise<TikTokAdsEntityPreviewResult> {
    const raw = asRecord(await this.request(
      `${this.connectionPath(input.projectId, input.connectionId)}/entities/preview`,
      { method: 'POST', body: JSON.stringify({ advertiserId: input.advertiserId, limit: 100 }), signal: input.signal },
      'Failed to preview TikTok Ads entities',
    ));
    const entities = asRecord(raw.entities);
    const diagnostics = asRecord(raw.providerDiagnostics);
    return {
      entities: Object.entries(entities).flatMap(([entityKind, value]) => (
        Array.isArray(value)
          ? value.map((item) => ({ ...asRecord(item), entityKind }))
          : []
      )),
      counts: countsFromEntities(entities),
      diagnostics: {
        bounded: true,
        hasMore: diagnostics.complete === false,
        warnings: strings(diagnostics.warnings),
      },
    };
  }

  async previewDelivery(input: {
    projectId: string;
    connectionId: string;
    advertiserId: string;
    since: string;
    until: string;
    signal?: AbortSignal;
  }): Promise<TikTokAdsDeliveryPreviewResult> {
    const raw = asRecord(await this.request(
      `${this.connectionPath(input.projectId, input.connectionId)}/delivery/preview`,
      {
        method: 'POST',
        body: JSON.stringify({ advertiserId: input.advertiserId, since: input.since, until: input.until, limit: 100 }),
        signal: input.signal,
      },
      'Failed to preview TikTok Ads delivery',
    ));
    const diagnostics = asRecord(raw.providerDiagnostics);
    return {
      rows: Array.isArray(raw.rows) ? raw.rows as Array<Record<string, unknown>> : [],
      providerDiagnostics: {
        bounded: diagnostics.bounded === true,
        hasMore: diagnostics.hasMore === true,
        sourceApiVersion: typeof diagnostics.sourceApiVersion === 'string' ? diagnostics.sourceApiVersion : undefined,
        warnings: strings(diagnostics.warnings),
      },
    };
  }

  async manualSync(input: {
    projectId: string;
    connectionId: string;
    advertiserId: string;
    reportDate: string;
    idempotencyKey: string;
  }): Promise<TikTokAdsSyncResult> {
    return await this.request(
      `${this.connectionPath(input.projectId, input.connectionId)}/sync/manual`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': input.idempotencyKey },
        body: JSON.stringify({ advertiserId: input.advertiserId, reportDate: input.reportDate }),
      },
      'Failed to synchronize TikTok Ads reporting data',
    ) as TikTokAdsSyncResult;
  }

  async disconnect(projectId: string, connectionId: string): Promise<void> {
    await this.request(
      `${this.connectionPath(projectId, connectionId)}/oauth/revoke`,
      { method: 'POST', body: '{}' },
      'Failed to disconnect TikTok Ads',
    );
  }
}

let singleton: TikTokAdsConnectService | null = null;

export function getTikTokAdsConnectService(): TikTokAdsConnectApi {
  if (!singleton) singleton = new TikTokAdsConnectService();
  return singleton;
}
